import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { notificationsApi } from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth, isCrewRole, isClientRole, isSalesRole } from '@/auth/AuthContext';
import { Layout, CrewLayout, PortalLayout } from '@/components/Layout';
import { Nav, PortalNav } from '@/components/Nav';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { NotificationInbox } from '@/components/NotificationInbox';
import {
  internalNavItems,
  salesNavItems,
  crewNavItems,
} from '@/pages/navItems';

// Simple per-channel on/off toggle over the allowlist-style channelPreferences model:
// absent = channel enabled for every category; ['__none__'] = channel disabled entirely
// (a non-empty allowlist that never matches a real notification category).
const CHANNEL_DISABLED_SENTINEL = '__none__';
const CHANNELS: Array<{ key: 'push' | 'sms' | 'email'; label: string; hint: string }> = [
  { key: 'push', label: 'Push', hint: 'Mobile / browser push notifications' },
  { key: 'sms', label: 'SMS', hint: 'Text message alerts' },
  { key: 'email', label: 'Email', hint: 'Email notifications' },
];

const TIMEZONES = ['Asia/Manila', 'Asia/Singapore', 'UTC', 'America/New_York', 'America/Los_Angeles'];

function isChannelDisabled(allowList: string[] | undefined): boolean {
  // Any non-empty allowlist that doesn't already cover "every category" reads as
  // "restricted" in this simple toggle UI — in practice it's always our sentinel.
  return Array.isArray(allowList) && allowList.length > 0;
}

export function NotificationPreferences() {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const { data: prefs, loading, error: loadError, reload } = useAsync(() => notificationsApi.getPreferences(), []);

  const [quietHoursStart, setQuietHoursStart] = useState('');
  const [quietHoursEnd, setQuietHoursEnd] = useState('');
  const [timezone, setTimezone] = useState('Asia/Manila');
  const [disabledChannels, setDisabledChannels] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!prefs) return;
    setQuietHoursStart(prefs.quietHoursStart ?? '');
    setQuietHoursEnd(prefs.quietHoursEnd ?? '');
    setTimezone(prefs.notificationTimezone || 'Asia/Manila');
    const disabled = new Set<string>();
    for (const ch of CHANNELS) {
      if (isChannelDisabled(prefs.channelPreferences?.[ch.key])) disabled.add(ch.key);
    }
    setDisabledChannels(disabled);
  }, [prefs]);

  const toggleChannel = (key: string) => {
    setDisabledChannels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const channelPreferences: Record<string, string[]> = {};
      for (const ch of CHANNELS) {
        if (disabledChannels.has(ch.key)) channelPreferences[ch.key] = [CHANNEL_DISABLED_SENTINEL];
      }
      await notificationsApi.updatePreferences({
        quietHoursStart: quietHoursStart || null,
        quietHoursEnd: quietHoursEnd || null,
        notificationTimezone: timezone,
        channelPreferences,
      });
      setMessage({ kind: 'ok', text: 'Preferences saved.' });
      reload();
    } catch (err) {
      setMessage({
        kind: 'error',
        text: (err as { message?: string }).message ?? 'Your preferences couldn\'t be saved. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div className="page notif-prefs-page">
      <div className="page-header">
        <p className="kicker">Account</p>
        <h1>Notifications</h1>
        <p className="text-muted">
          Your inbox, plus which channels reach you and when. Some urgent alerts are always delivered.
        </p>
        <p className="notif-password-row" style={{ marginTop: 'var(--space-sm)' }}>
          <Link className="notif-password-link" to="/account/password">Change your password</Link>
        </p>
      </div>

      <div className="notif-inbox-wrap">
        <NotificationInbox />
      </div>

      <h2 className="notif-prefs-heading">Preferences</h2>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : loadError ? (
        <p className="text-urgent" role="alert">
          Your preferences couldn&apos;t be loaded.{' '}
          <Button size="sm" variant="secondary" type="button" onClick={() => reload()}>Retry</Button>
        </p>
      ) : (
        <form className="notif-prefs-form" onSubmit={handleSubmit}>
          <section className="notif-prefs-section">
            <h2>Channels</h2>
            <p className="text-muted" style={{ marginTop: '-0.5rem', fontSize: '0.8125rem' }}>
              Turn a channel off to stop routine notifications from using it. Some urgent alerts are
              always delivered.
            </p>
            {CHANNELS.map((ch) => (
              <label key={ch.key} className="notif-channel-row">
                <div className="notif-channel-text">
                  <strong>{ch.label}</strong>
                  <span className="text-muted notif-channel-hint"><span className="notif-channel-dash"> — </span>{ch.hint}</span>
                </div>
                <input
                  type="checkbox"
                  checked={!disabledChannels.has(ch.key)}
                  onChange={() => toggleChannel(ch.key)}
                />
              </label>
            ))}
          </section>

          <section className="notif-prefs-section">
            <h2>Quiet hours</h2>
            <p className="text-muted" style={{ marginTop: '-0.5rem', fontSize: '0.8125rem' }}>
              Push and SMS notifications wait until quiet hours end. Some urgent alerts are always
              delivered. Email is not affected.
            </p>
            <div className="grid-2 notif-quiet-grid">
              <Input
                label="Starts"
                type="time"
                value={quietHoursStart}
                onChange={(e) => setQuietHoursStart(e.target.value)}
              />
              <Input
                label="Ends"
                type="time"
                value={quietHoursEnd}
                onChange={(e) => setQuietHoursEnd(e.target.value)}
              />
            </div>
            <label className="field-label" htmlFor="notif-timezone">Timezone</label>
            <select
              id="notif-timezone"
              className="input-field"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              style={{ marginBottom: 'var(--space-md)' }}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </section>

          {message && (
            <p className={message.kind === 'error' ? 'text-urgent' : 'text-ok'} role={message.kind === 'error' ? 'alert' : 'status'}>
              {message.text}
            </p>
          )}
          <Button type="submit" className="notif-save" loading={saving}>Save preferences</Button>
        </form>
      )}

      <style>{`
        .notif-inbox-wrap { margin-bottom: var(--space-xl); max-width: 720px; }
        .notif-prefs-heading { font-size: 1.125rem; margin-bottom: var(--space-md); }
        .notif-prefs-form {
          max-width: 560px;
          display: flex;
          flex-direction: column;
          gap: var(--space-lg);
        }
        .notif-prefs-section {
          background: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .notif-prefs-section h2 { margin-bottom: var(--space-xs); font-size: 1rem; }
        .notif-channel-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
          cursor: pointer;
        }
        .notif-channel-row:last-child { border-bottom: none; }
        .field-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--color-text-muted);
          margin-bottom: var(--space-xs);
        }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-md); }
        @media (max-width: 480px) { .grid-2 { grid-template-columns: 1fr; } }
        .portal-nav { margin-bottom: var(--space-lg); }
      `}</style>
    </div>
  );

  return <RoleShell role={role}>{content}</RoleShell>;
}

export function RoleShell({ role, children }: { role: string; children: ReactNode }) {
  if (isCrewRole(role)) {
    return (
      <CrewLayout>
        <div className="crew-content">
          <Nav items={crewNavItems} variant="crew" />
          {children}
        </div>
      </CrewLayout>
    );
  }

  if (isClientRole(role)) {
    return (
      <PortalLayout>
        <div className="portal-nav"><PortalNav variant="top" /></div>
        {children}
      </PortalLayout>
    );
  }

  const navItems = isSalesRole(role) ? salesNavItems : internalNavItems;
  return <Layout navItems={navItems}>{children}</Layout>;
}
